import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ListResult } from '../api/core/list-result';
import { SocCodeLookupItem, ProcessTemplateLookupItem, FNLookupItem, QuestionnaireLookupItem, MailRecipientLookupItem } from '../api/data-model';
import { Audience } from '../api/enum';
import { KeywordLookupItem } from '../api/lookup/keyword-lookup-item';
import { LookupItem } from '../api/lookup/lookup-item';
import { SearchInput } from '../api/search/search-input';
import { SearchResult } from '../api/search/search-result';
import { BACKEND_URL } from '../app.config';

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  constructor(private readonly http: HttpClient, @Inject(BACKEND_URL) private readonly baseUrl: string) {}

  _normalizeParam(param: string | string[] | number | boolean | undefined) {
    if (!param) {
      return '';
    }
    if (typeof param == 'string') {
      return param.trim();
    }

    if (Array.isArray(param)) {
      return param.join(',');
    }

    return param;
  }

  _getGenericLookup<T extends LookupItem = LookupItem>(url: string) {
    return (keys: string[] | undefined = undefined, filter: string | undefined = undefined, maxCount: number = 20): Observable<ListResult<T>> =>
      this.http.get<ListResult<T>>(`${this.baseUrl}${url}`, {
        params: {
          keys: this._normalizeParam(keys),
          filter: this._normalizeParam(filter),
          maxCount,
        },
      });
  }

  getUsers(
    keys: string | string[] | undefined = undefined,
    filter: string | undefined = undefined,
    roles: string[] | undefined = undefined,
    maxCount: number = 20,
    isSigningAttorney: boolean | undefined = undefined
  ): Observable<ListResult<LookupItem>> {
    return this.http.get<ListResult<LookupItem>>(`${this.baseUrl}/api/app/lookup/users`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        roles: this._normalizeParam(roles),
        maxCount,
        isSigningAttorney: isSigningAttorney !== undefined ? isSigningAttorney : '',
      },
    });
  }

  getForeignNationals(
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    companyId: string | undefined = undefined,
    relativeOf: string | undefined = undefined,
    matterId: string | undefined = undefined,
    maxCount: number = 20
  ): Observable<ListResult<FNLookupItem>> {
    return this.http.get<ListResult<FNLookupItem>>(`${this.baseUrl}/api/app/lookup/foreign-nationals`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        companyId: this._normalizeParam(companyId),
        relativeOf: this._normalizeParam(relativeOf),
        matterId: this._normalizeParam(matterId),
        maxCount,
      },
    });
  }

  getCompanyContacts(
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    companyId: string | undefined = undefined,
    roles: number | undefined = undefined,
    maxCount: number = 20
  ): Observable<ListResult<LookupItem>> {
    return this.http.get<ListResult<LookupItem>>(`${this.baseUrl}/api/app/lookup/company-contacts`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        companyId: this._normalizeParam(companyId),
        roles: this._normalizeParam(roles),
        maxCount,
      },
    });
  }

  getMatters(
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    foreignNationalId: string | undefined = undefined,
    maxCount: number = 20
  ): Observable<ListResult<LookupItem>> {
    return this.http.get<ListResult<LookupItem>>(`${this.baseUrl}/api/app/lookup/matters`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        foreignNationalId: this._normalizeParam(foreignNationalId),       
        maxCount,
      },
    });
  }

  getQuestionnaires(
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    audience: number[] = [],
    matterId: string | undefined = undefined,
    maxCount: number = 20
  ): Observable<ListResult<QuestionnaireLookupItem>> {
    return this.http.get<ListResult<QuestionnaireLookupItem>>(`${this.baseUrl}/api/app/lookup/questionnaires`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        audience: audience,
        matterId: this._normalizeParam(matterId),
        maxCount,
      },
    });
  }

  getMailRecipients(
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    mailRecipientType: number = 0,
    matterId: string | undefined = undefined,
    companyId: string | undefined = undefined,
    relativeOf: string | undefined = undefined,
    maxCount: number = 20
  ): Observable<ListResult<MailRecipientLookupItem>> {
    return this.http.get<ListResult<MailRecipientLookupItem>>(`${this.baseUrl}/api/app/lookup/mail-recipients`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        mailRecipientType: mailRecipientType,
        matterId: this._normalizeParam(matterId),
        companyId: this._normalizeParam(companyId),
        relativeOf: this._normalizeParam(relativeOf),
        maxCount,
      },
    });
  }

  getForms(
    publishedOnly: boolean = false,
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    maxCount: number = 20,
    forCopy: boolean = false
  ): Observable<ListResult<KeywordLookupItem>> {
    return this.http.get<ListResult<KeywordLookupItem>>(`${this.baseUrl}/api/app/lookup/forms`, {
      params: {
        publishedOnly: publishedOnly,
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        maxCount,
        forCopy,
      },
    });
  }

  getKeywords(
    keys: string[] | undefined = undefined,
    filter: string | undefined = undefined,
    writableOnly: boolean = false,
    lookupOnly: boolean = false,
    linkableOnly: boolean = false,
    includeDetails: boolean = false,
    audience: Audience = Audience.Internal,
    formId: string = '',
    maxCount: number = 20
  ): Observable<ListResult<KeywordLookupItem>> {
    return this.http.get<ListResult<KeywordLookupItem>>(`${this.baseUrl}/api/app/lookup/keywords`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        writableOnly: writableOnly,
        lookupOnly: lookupOnly,
        linkableOnly: linkableOnly,
        includeDetails: includeDetails,
        audience: audience,
        formId: formId,
        maxCount,
      },
    });
  }

  postKeywordsBulk(keywords: string[], includeDetails: boolean = true, audience: Audience = Audience.Internal): Observable<ListResult<KeywordLookupItem>> {
    return this.http.post<ListResult<KeywordLookupItem>>(`${this.baseUrl}/api/app/lookup/keywords`, keywords, {
      params: {
        includeDetails: includeDetails,
        audience: audience
      }
    });
  }

  getProcessModules = this._getGenericLookup('/api/app/lookup/process-modules');

  getAllProcessModules(): Observable<ListResult<LookupItem>> {
    return this.http.get<ListResult<LookupItem>>(`${this.baseUrl}/api/app/lookup/process-modules`, {
      params: {
        maxCount: 1000,
      },
    });
  }

  getProcessTemplates = this._getGenericLookup<ProcessTemplateLookupItem>('/api/app/lookup/process-templates');

  // getProcessTemplateVersions = this._getGenericLookup<ProcessTemplateLookupItem>('/api/app/lookup/process-template-versions');
  getProcessTemplateVersions(keys: string[] | undefined = undefined, filter: string | undefined = undefined, maxCount: number = 20): Observable<ListResult<LookupItem>> {
    return this.http.get<ListResult<LookupItem>>(`${this.baseUrl}/api/app/lookup/process-template-versions`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        maxCount,
      },
    });
  }
  getProgressStatuses = this._getGenericLookup('/api/app/lookup/progress-statuses');

  getCompanies = this._getGenericLookup('/api/app/lookup/companies');

  // getLetters = this._getGenericLookup(' /api/app/lookup/letters');
  getLetters(keys: string[] | undefined = undefined, filter: string | undefined = undefined, maxCount: number = 20): Observable<ListResult<LookupItem>> {
    return this.http.get<ListResult<LookupItem>>(`${this.baseUrl}/api/app/lookup/letters`, {
      params: {
        keys: this._normalizeParam(keys),
        filter: this._normalizeParam(filter),
        maxCount,
      },
    });
  }

  // getfns = this._getGenericLookup('/api/app/lookup/fns');

  //getQuestionnaires = this._getGenericLookup('/api/app/lookup/questionnaires');

  getSOCCodes = this._getGenericLookup<SocCodeLookupItem>('/api/app/lookup/soc-codes');


  getShippers = this._getGenericLookup('/api/app/lookup/shippers');

  search(paras: SearchInput): Observable<SearchResult> {
    return this.http.post<SearchResult>(`${this.baseUrl}/api/app/lookup/search`, paras);
  }
}
